import { Btn } from 'components/Btn'
import { Heading } from 'components/Heading'
import { Icon, IconType } from 'components/Icon'
import { useState } from 'utils'
import { ReactNode, useCallback, useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'
import { useAccordion } from './Accordion.provider'

const AccordionView = styled.div`
	.ctrl {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		padding: 0.75em 0;
		width: 100%;
		color: ${props => props.theme.colors.secondary.val};

		h4 {
			color: inherit;
			padding: 0;
			margin: 0;
		}

		&:hover {
		}
		.triangle-wrap {
			color: inherit;
			transition: transform ${props => props.theme.times.tranS};
			.icon {
				display: block;
				max-width: 15px;
				margin-left: 0.5em;
				color: inherit;
			}
		}
		.icon {
			display: block;
			margin: 0 0.5em;
			color: inherit;
		}
	}
	.ctrl-group {
		overflow: hidden;
		transition: all ${props => props.theme.times.tranS};
		.inner-wrap {
			padding-bottom: 1em;
		}
	}
`

type AccordionRenderFunc = (options: AccordionRenderFuncOptions) => ReactNode

type AccordionToggleFunc = (override?: boolean) => void

export type AccordionRenderFuncOptions = {
	toggle: (override?: boolean) => void
	isOpen: boolean
	disable: React.Dispatch<React.SetStateAction<boolean | undefined>>
}

export type AccordionProps = {
	ctrl: string | ((toggle: AccordionToggleFunc) => ReactNode)
	children: ReactNode | AccordionRenderFunc
	ctrlIcon?: IconType
	startsOpen?: boolean
	afterOpen?: () => void
	afterClose?: () => void
	override?: boolean
	toggleHandler?: AccordionToggleFunc
	className?: string
	disabled?: boolean
}
export const Accordion = ({
	children,
	startsOpen = false,
	afterClose,
	afterOpen,
	ctrl,
	ctrlIcon,
	override: parentState,
	toggleHandler: parentToggle,
	className,
	disabled: forcedDisabled,
}: AccordionProps) => {
	const inner = useRef<null | HTMLDivElement>(null)
	const height = inner.current?.offsetHeight
	const isInitialRender = useRef(true)

	const [openState, setOpenState] = useState<'open' | 'closed' | 'opening' | 'closing'>(
		startsOpen ? 'open' : 'closed',
		'openState'
	)

	const [disabled, setDisabled] = useState(forcedDisabled, 'disabled')

	const toggle: AccordionToggleFunc = useCallback(
		(override?: boolean) => {
			if (typeof parentState === 'boolean' && parentToggle) parentToggle(override)

			if (typeof override === 'boolean') {
				setOpenState(override ? 'opening' : 'closing')
			} else {
				setOpenState(state => (state === 'closed' || state === 'closing' ? 'opening' : 'closing'))
			}
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[parentState]
	)

	const { times } = useTheme()
	useEffect(() => {
		if (openState === 'opening' || openState === 'closing')
			setTimeout(() => {
				setOpenState(state => {
					if (state === 'opening' && afterOpen) afterOpen()
					else if (afterClose) afterClose()

					return state === 'opening' ? 'open' : 'closed'
				})
			}, times.short)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openState])

	useEffect(() => {
		// We check for initial render to solve case where parentState might cause an initial state of 'closing'
		if (typeof parentState === 'boolean' && !isInitialRender.current)
			setOpenState(parentState ? 'opening' : 'closing')

		isInitialRender.current = false
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentState])

	const isOpen = openState === 'open' || openState === 'opening'

	const options: AccordionRenderFuncOptions = {
		toggle,
		isOpen,
		disable: setDisabled,
	}

	const contents: ReactNode = typeof children === 'function' ? children(options) : children

	let maxHeight = '0px'

	switch (openState) {
		case 'opening':
			maxHeight = `${height}px`
			break
		case 'open':
			maxHeight = 'initial'
			break
		case 'closing':
			maxHeight = `${height}px`
			break
		case 'closed':
			maxHeight = '0px'
			break
		default:
			break
	}

	return (
		<AccordionView
			className={`accordion${className ? ` ${className}` : ''}${isOpen ? ' __open' : ' __closed'}`}
		>
			{typeof ctrl === 'function' ? (
				ctrl(toggle)
			) : (
				<Btn onClick={() => toggle()} className={`ctrl`} disabled={disabled}>
					{ctrlIcon ? <Icon type={ctrlIcon} /> : null}
					<Heading displayType='p-heading'>{ctrl}</Heading>
					<div className='triangle-wrap' style={{ transform: `scaleY(${isOpen ? '-1' : '1'})` }}>
						<Icon type='triangle' className={`arrow`} />
					</div>
				</Btn>
			)}
			<div
				className={`ctrl-group${isOpen ? ' __expanded' : ' __collapsed'}`}
				style={{
					maxHeight,
				}}
			>
				<div className='inner-wrap' ref={inner}>
					{contents}
				</div>
			</div>
		</AccordionView>
	)
}

type AccordionConsumerProps = Pick<
	AccordionProps,
	'children' | 'className' | 'ctrl' | 'startsOpen' | 'ctrlIcon' | 'disabled'
> & {
	id: number
}

export const AccordionConsumer = ({ id, ...accordionProps }: AccordionConsumerProps) => {
	const { openIds, toggle } = useAccordion()

	const isOpen = openIds.includes(id)

	return <Accordion {...accordionProps} override={isOpen} toggleHandler={() => toggle(id)} />
}
